// Kundenakte öffnet sich beim Anruf von selbst.
//
// Destiny, 08.09.2026: „Wenn man jemand anklingelt, ob die Anfrage direkt
// öffnet ODER man unten links beim Anruf-Fenster auf die Kunden klicken kann,
// sodass es sich öffnet — spart mir die Zeit, die Kunden im System zu suchen
// während des Calls."
//
// Beides ist umgesetzt: die Namen im Anruf-Fenster und im Power Dialer sind
// anklickbar, und sobald es klingelt, steht die Akte ohnehin schon offen. Das
// automatische Öffnen lässt sich in den Einstellungen abschalten — es wechselt
// die Seite im Hauptfenster, und wer lieber in der Anrufliste stehen bleibt,
// soll das können. Gespräch und Anruf-Fenster laufen dabei weiter: sie liegen
// über der Seite und überleben jeden Seitenwechsel.

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDialerStore } from '../stores/dialerStore';

const SCHLUESSEL = 'akte-beim-anruf';

/** An, solange niemand ausdrücklich abgeschaltet hat — so war es gewünscht. */
export function akteBeimAnrufAn(): boolean {
  try {
    return localStorage.getItem(SCHLUESSEL) !== 'aus';
  } catch {
    return true; // Privatmodus: dann eben ohne Gedächtnis
  }
}

export function akteBeimAnrufSetzen(an: boolean): void {
  try {
    localStorage.setItem(SCHLUESSEL, an ? 'an' : 'aus');
  } catch { /* Privatmodus: egal */ }
}

export interface AnrufLage {
  /** Kundensatz der Person am anderen Ende; null bei Direktwahl ohne Akte. */
  customerId: string | null;
  /** Pfad, der im Hauptfenster gerade offen ist. */
  pfad: string;
  /** Einstellung „Kundenakte beim Anruf öffnen". */
  automatisch: boolean;
}

/**
 * Wohin gesprungen wird, wenn ein Anruf losgeht — oder `null`, wenn die Seite
 * bleiben soll.
 */
export function akteZiel({ customerId, pfad, automatisch }: AnrufLage): string | null {
  if (!automatisch || !customerId) return null;
  const ziel = `/customers/${customerId}`;
  if (pfad === ziel) return null;
  // Terminsuche nicht wegreißen: dort wird während des Gesprächs das
  // Probetraining gebucht (Lilly, 19.08.2026). Ein automatischer Sprung würde
  // eine halb ausgefüllte Buchung kosten — genau beim nächsten Anruf, wenn der
  // Dialer von selbst weiterwählt.
  if (pfad.startsWith('/availability')) return null;
  return ziel;
}

/** Was ein Klick auf einen Namen bewirken soll. */
export type NamensKlick = 'oeffnen' | 'fenster-wegraeumen' | 'nichts';

/**
 * Ein Klick auf den Namen führt in die Akte — nur liegt die beim Telefonieren
 * meistens schon hinter dem Anruf-Fenster, weil sie sich beim Wählen von selbst
 * geöffnet hat. Ein Sprung dorthin ändert dann für das Auge nichts, und der
 * Name wirkt kaputt („wenn ich auf den Namen klicke, passiert nix", 08.09.2026).
 * Also ist der Klick in dieser Lage ein „zeig sie mir": das Fenster macht sich
 * klein und gibt die Akte frei.
 */
export function namensKlick(
  { ziel, pfad, fensterOffen }: { ziel: string; pfad: string; fensterOffen: boolean },
): NamensKlick {
  if (pfad !== ziel) return 'oeffnen';
  return fensterOffen ? 'fenster-wegraeumen' : 'nichts';
}

/** Was beim Darüberfahren am Namen steht — überall derselbe Wortlaut. */
export function namensTitel(klick: NamensKlick): string {
  switch (klick) {
    case 'fenster-wegraeumen':
      return 'Die Akte liegt hinter diesem Fenster — Klick räumt das Fenster weg';
    case 'nichts':
      return 'Diese Kundenakte ist gerade offen';
    default:
      return 'Kundenakte öffnen — das Gespräch läuft weiter';
  }
}

/**
 * Öffnet bei jedem neuen Wählversuch die Akte der angerufenen Person. Gehört
 * ins Layout — dort läuft es unabhängig davon, welche Seite offen ist.
 */
export function useAkteBeimAnruf(): void {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Der Wählversuchs-Zähler ist das verlässliche „neuer Anruf"-Signal: die
  // Anruf-Kennung der Telefonanlage trifft erst verzögert ein.
  const versuch = useDialerStore((s) => s.callAttempt);
  const current = useDialerStore((s) => s.current);
  const zuletzt = useRef(0);

  useEffect(() => {
    if (versuch === 0 || versuch === zuletzt.current) return;
    zuletzt.current = versuch;
    const ziel = akteZiel({
      customerId: current && !current.ad_hoc ? current.customer_id : null,
      pfad: pathname,
      automatisch: akteBeimAnrufAn(),
    });
    if (ziel) navigate(ziel);
  }, [versuch, current, pathname, navigate]);
}
